import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icons } from "@/components/Icons";
import { useAuth } from "@/lib/auth";
import { ApiError, get } from "@/lib/api";

interface SsoMatch {
  id: number;
  kind: string;
  label: string;
  tenant_name?: string;
  redirect_url: string;
  is_enforced: boolean;
}

const KIND_LABEL: Record<string, string> = {
  azure: "Microsoft",
  google: "Google",
  okta: "Okta",
  oidc: "SSO",
  saml: "SSO",
};

type Step = "email" | "method";

/**
 * Two-step login.
 *
 * Step 1: email-only. On Continue we call /auth/sso/discover with the
 *         email's domain (purely public domain info — no per-user lookup,
 *         so this can't be used to enumerate accounts).
 * Step 2: branch on what discovery returned:
 *           - SSO matches + must_use_sso → SSO buttons only.
 *           - SSO matches + not enforced → SSO buttons + "Use password instead" link.
 *           - No SSO matches             → password field.
 */
export default function Login() {
  const { login, user, loading } = useAuth();
  const nav = useNavigate();

  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [ssoMatches, setSsoMatches] = useState<SsoMatch[]>([]);
  const [ssoEnforced, setSsoEnforced] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [portalCode, setPortalCode] = useState("");
  const [portalCodeNeeded, setPortalCodeNeeded] = useState(false);

  const passwordInput = useRef<HTMLInputElement>(null);

  // If we hit /login with an existing valid session, just bounce to
  // the dashboard. This stops the confusing "login fails but refresh
  // signs me in" loop where a stale session was already authenticated
  // and the form CSRF mismatched against a different state.
  useEffect(() => {
    if (!loading && user) nav("/", { replace: true });
  }, [loading, user, nav]);

  // When we move into step 2 with the password field visible, autofocus it.
  useEffect(() => {
    if (step === "method" && showPassword) {
      passwordInput.current?.focus();
    }
  }, [step, showPassword]);

  async function discover(e?: React.FormEvent) {
    e?.preventDefault();
    if (!email.includes("@") || email.split("@")[1].length < 2) {
      setError("Enter a valid email address.");
      return;
    }
    setDiscovering(true);
    setError(null);
    try {
      const res = await get<{ connections: SsoMatch[]; must_use_sso: boolean }>(
        `/v1/auth/sso/discover?email=${encodeURIComponent(email)}`,
      );
      setSsoMatches(res.connections ?? []);
      setSsoEnforced(!!res.must_use_sso);
      // Show password unless SSO is the only option for this domain.
      const hasSso = (res.connections ?? []).length > 0;
      setShowPassword(!hasSso);
      setStep("method");
    } catch (err) {
      // Discovery failure → fall back to password to avoid blocking sign-in.
      setSsoMatches([]);
      setSsoEnforced(false);
      setShowPassword(true);
      setStep("method");
      // Surface the error inline but still let them try password.
      const msg = err instanceof ApiError ? err.message : (err as Error).message;
      setError(`Could not check SSO for this address (${msg}). You can still sign in with a password.`);
    } finally {
      setDiscovering(false);
    }
  }

  async function submitPassword(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const r = await login(email, password, portalCode || undefined);
      if (r.portal) {
        if (r.twoFactorRequired) { setPortalCodeNeeded(true); return; }
        window.location.assign("/portal");   // full load so the portal session is picked up
        return;
      }
      nav(r.twoFactorRequired ? "/auth/2fa" : "/", { replace: true });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : (err as Error).message;
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  function backToEmail() {
    setStep("email");
    setError(null);
    setPassword("");
    setShowPassword(false);
    setSsoMatches([]);
    setSsoEnforced(false);
  }

  return (
    <div className="auth-shell">
      <div className="card" style={{ width: 380, padding: 28 }}>
        <div className="row" style={{ gap: 10, marginBottom: 18 }}>
          <span className="brand-mark" style={{ width: 36, height: 36 }}>
            <Icons.Logo size={20} stroke="var(--green-500)" />
          </span>
          <div>
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 18 }}>Control</div>
            <div className="kicker" style={{ fontSize: 10 }}>by Jowcey</div>
          </div>
        </div>
        <h1 style={{ fontSize: 22, marginBottom: 4 }}>Welcome back</h1>
        <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>Sign in to continue.</p>

        {step === "email" ? (
          <form onSubmit={discover} className="col" style={{ gap: 14, marginTop: 18 }}>
            <div className="field">
              <label htmlFor="email">Email</label>
              <input
                id="email" className="input" type="email" required autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={discovering}
              />
            </div>
            {error && (
              <div className="pill pill-fail" style={{ alignSelf: "flex-start" }}>{error}</div>
            )}
            <button type="submit" className="btn btn-primary btn-lg"
                    disabled={discovering || !email}
                    style={{ marginTop: 4, width: "100%", justifyContent: "center" }}>
              {discovering ? "Checking…" : "Continue"}
            </button>
          </form>
        ) : (
          <div className="col" style={{ gap: 14, marginTop: 18 }}>
            {/* Show the email at the top with a "change" link so the user
                doesn't have to retype if they made a typo. */}
            <div className="row" style={{ justifyContent: "space-between", alignItems: "center", fontSize: 13 }}>
              <span className="muted">Signing in as <b style={{ color: "var(--warm-text)" }}>{email}</b></span>
              <button type="button" className="btn btn-ghost btn-sm" onClick={backToEmail}>Change</button>
            </div>

            {ssoMatches.length > 0 && (
              <div className="col" style={{ gap: 8 }}>
                <div className="muted" style={{ fontSize: 12 }}>
                  {ssoEnforced
                    ? "Your organisation requires single sign-on:"
                    : "Your organisation supports single sign-on:"}
                </div>
                {ssoMatches.map((m) => (
                  <a key={m.id} href={m.redirect_url}
                     className="btn btn-secondary"
                     style={{ width: "100%", justifyContent: "center" }}>
                    <Icons.Shield size={14} /> Continue with {KIND_LABEL[m.kind] ?? "SSO"}
                    {m.tenant_name && <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>· {m.tenant_name}</span>}
                  </a>
                ))}
              </div>
            )}

            {/* Password option:
                  - Always shown if no SSO matched
                  - Shown as an opt-in link if SSO matched but not enforced
                  - Hidden entirely if SSO is enforced for this domain    */}
            {!ssoEnforced && !showPassword && ssoMatches.length > 0 && (
              <button type="button" className="btn btn-ghost btn-sm"
                      onClick={() => setShowPassword(true)}
                      style={{ alignSelf: "center", marginTop: 4 }}>
                Use password instead
              </button>
            )}

            {!ssoEnforced && showPassword && (
              <form onSubmit={submitPassword} className="col" style={{ gap: 14 }}>
                <div className="field">
                  <label htmlFor="password">Password</label>
                  <input
                    id="password" ref={passwordInput} className="input" type="password" required
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                  />
                </div>
                {portalCodeNeeded && (
                  <div className="field">
                    <label htmlFor="portal-code">Verification code</label>
                    <input id="portal-code" className="input" inputMode="numeric" autoComplete="one-time-code" required
                           value={portalCode} onChange={(e) => setPortalCode(e.target.value)} autoFocus />
                    <span className="muted" style={{ fontSize: 11.5 }}>From your authenticator app.</span>
                  </div>
                )}
                {error && (
                  <div className="pill pill-fail" style={{ alignSelf: "flex-start" }}>{error}</div>
                )}
                <button type="submit" className="btn btn-primary btn-lg" disabled={submitting}
                        style={{ marginTop: 4, width: "100%", justifyContent: "center" }}>
                  {submitting ? "Signing in…" : "Sign in"}
                </button>
              </form>
            )}

            {ssoEnforced && error && (
              <div className="pill pill-fail" style={{ alignSelf: "flex-start" }}>{error}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
